import { DestinationParamsState } from '@anm/api/modules/stream';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LiveStreamKind } from 'types/stream';

import { streamActions, streamSelectors } from '../../../store/modules/stream';

export const useStopStream = (streamId: string) => {
  const dispatch = useDispatch();

  const handleStopStream = useCallback(() => dispatch(streamActions.stopStream(streamId)), [streamId]);

  return handleStopStream;
};

type ModalProps = {
  kind: LiveStreamKind;
  streamId: string;
  projectId: string;
  openEditStreamModal(): void;
  closeEditStreamModal(): void;
};

export const useDuplicateStream = ({
  projectId,
  streamId,
  kind,
  openEditStreamModal,
  closeEditStreamModal
}: ModalProps) => {
  const dispatch = useDispatch();
  const isDuplicate = useRef(false);
  const projectToStream = useRef<string>();
  const isRecording = kind === 'RECORD';

  const isPendingDuplicate = useSelector(streamSelectors.selectIsPendingDuplicateStream);
  const createStreamParams = useSelector(streamSelectors.selectCreateStreamParams);
  const isCreateStreamPending = useSelector(streamSelectors.selectCreateStreamPending);
  const stream = useSelector(state => streamSelectors.selectStreamById(state, streamId));

  useEffect(() => {
    if (!createStreamParams || isCreateStreamPending) return;

    closeEditStreamModal();
  }, [createStreamParams, isCreateStreamPending]);

  useEffect(() => {
    !isPendingDuplicate && closeEditStreamModal();
  }, [isPendingDuplicate]);

  const handleDuplicate = useCallback(() => {
    isDuplicate.current = true;
    if (isRecording) {
      stream &&
        dispatch(
          streamActions.duplicateStream({
            input: '',
            schedule: null,
            isDuplicate: true,
            title: stream.meta.title,
            webinar: !!stream.meta.webinarId,
            preview: stream.meta.preview,
            description: stream.meta.description,
            kind: stream.kind,
            roomId: stream.meta.roomId
          })
        );
    } else {
      openEditStreamModal();
    }
  }, [stream]);

  const handleStreamVideo = useCallback(() => {
    projectToStream.current = projectId;
    handleDuplicate();
  }, []);

  return [isDuplicate, projectToStream, handleDuplicate, handleStreamVideo] as const;
};

export const useEditStreamTitle = (streamId: string) => {
  const dispatch = useDispatch();

  const stream = useSelector(state => streamSelectors.selectStreamById(state, streamId));

  const handelEditStreamTitle = (newTitle: string) => {
    if (!stream || newTitle === stream.meta.title) return;

    const {
      kind,
      input,
      schedule,
      destinations,
      meta: { folderId, description, preview, title: oldTitle, webinarId }
    } = stream;

    const destinationParams = destinations.reduce((acc, item) => {
      const { title } = item.request;
      const destinationTitle = !title || title === oldTitle ? newTitle : title;

      acc[item.destination_id] = {
        ...item.request,
        title: destinationTitle
      };

      return acc;
    }, {} as DestinationParamsState);

    const data = {
      kind,
      input,
      preview,
      streamId,
      folderId,
      description,
      destinationParams,
      title: newTitle,
      webinar: !!webinarId,
      isRefetchList: true,
      schedule: schedule ? new Date(schedule).getTime() : null
    };

    dispatch(streamActions.editStream(data));
  };

  return handelEditStreamTitle;
};

type CloseProps = {
  isOpenModal: boolean;
  roomId: string;
  closeModal(): void;
};

export const useCloseModalAfterSettingsUpdated = ({ isOpenModal, closeModal, roomId }: CloseProps) => {
  const dispatch = useDispatch();
  const savedData = useSelector(streamSelectors.selectSaveSettingsData);
  const settingsParams = useSelector(streamSelectors.selectSaveSettingsParams);
  const isEditPending = useSelector(streamSelectors.selectEditStreamIsPending);

  useEffect(() => {
    if (!isEditPending && isOpenModal && savedData && settingsParams?.roomId === roomId) {
      setTimeout(closeModal, 0);
    }
  }, [isEditPending, savedData, settingsParams?.roomId]);

  useEffect(
    () => () => {
      dispatch(streamActions.clearRoomSettings());
    },
    []
  );
};

export const useEditDestinations = (openEditStreamModal: () => void) => {
  const isEditDestinations = useRef(false);

  const handelEditDestinations = () => {
    isEditDestinations.current = true;
    openEditStreamModal();
  };

  return [isEditDestinations, handelEditDestinations] as const;
};
